import { FC, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface MultipleSelectOption {
  id: string;
  label: string;
}

interface GenericMultipleSelectProps<T extends MultipleSelectOption> {
  options: T[];
  placeholder: string;
  inputValues: T[];
  setValues: (values: T[]) => void;
  width?: string;
  disabled?: boolean;
}

const GenericMultipleSelect: FC<
  GenericMultipleSelectProps<MultipleSelectOption>
> = ({
  options,
  placeholder,
  inputValues,
  setValues,
  width = "100%",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const selectedIds = new Set(inputValues.map((v) => v.id));

  const handleToggle = (option: MultipleSelectOption) => {
    if (selectedIds.has(option.id)) {
      setValues(inputValues.filter((v) => v.id !== option.id));
    } else {
      setValues([...inputValues, option]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={cn(
            "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 flex min-h-9 items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-1.5 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 dark:hover:bg-input/50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          )}
          style={{ width, minWidth: "5rem" }}
        >
          <span className="flex flex-1 flex-wrap gap-1 overflow-hidden">
            {inputValues.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              inputValues.map((val) => (
                <Badge key={val.id} variant="secondary" className="text-xs">
                  {val.label}
                </Badge>
              ))
            )}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-1"
        align="start"
      >
        <div className="max-h-[300px] overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              className="flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground"
              onClick={() => handleToggle(option)}
            >
              <Checkbox
                checked={selectedIds.has(option.id)}
                tabIndex={-1}
                className="pointer-events-none"
              />
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default GenericMultipleSelect;
